/**
 * @module engine3d/models
 * @summary The type → 3D model registry, the 2D-to-3D size rules, and the
 *   family fallbacks that keep unmapped and custom symbols looking right.
 * @description
 *   The 2D document has no third axis, so this module supplies one. A symbol's
 *   2D `w` becomes the model's X extent and its 2D `h` becomes the HEIGHT (Y) —
 *   that is what makes a 50 × 100 silo read as tall and a 90 × 25 conveyor read
 *   as long and low. The missing Z depth comes from `DEPTH` below: round
 *   vessels are as deep as they are wide, machines that run along the line are
 *   deep enough to walk beside, and everything else falls back to a sensible
 *   proportion of its 2D footprint.
 *
 *   Not every one of the ~90 symbols has a bespoke model, and custom symbols
 *   can't have one by definition. Those resolve through `FAMILY_FALLBACK`,
 *   which picks an archetype from the symbol's LIBRARY CATEGORY — so a new
 *   custom valve still comes out looking like a valve.
 *
 *   KIT v2 hooks: family modules register extra builders through their own
 *   `EXTRA_MODELS` export (merged below, before `MODEL_CHOICES`), and every
 *   build runs inside `withDetail(lod, …)` with the lod in the cache key.
 *
 *   // INVARIANT: models are reachable from a type key, a family fallback, or
 *   //   an explicit `model3` pin — never newly from a label (no additions to
 *   //   `NAME_ALIASES`; see the `model3` doctrine in docs/TECHNICAL-GUIDE.md).
 *   // HAZARD: `groundModel` and `dockPorts` copy ports; spread the original so
 *   //   optional fields (`Port3.r`) are not silently dropped.
 */
import { DEFS } from '../../engine/shapes/defs';
import { registry } from '../../engine/shapes/registry';
import type { Builder, ExtraModels, Model3 } from './kit';
import {
  assy,
  baseFrame,
  bevelBox,
  estop,
  hmiPanel,
  moveA,
  namePlate,
  nozzleRF,
  p,
  rectFoot,
  rotateX,
  roundBox,
  translate,
  v3,
  withDetail,
  zm,
} from './kit';
import { closestOnMesh } from '../mesh';
import * as storage from './storage';
import * as conveying from './conveying';
import * as process from './process';
import * as downstream from './downstream';
import * as utility from './utility';
import * as cable from './cable';

// ─── The bespoke model table ─────────────────────────────────────────────

const BUILDERS: Record<string, Builder> = {
  // Storage & Silos
  silo: storage.silo,
  hopper: storage.hopper,
  htank: storage.htank,
  vtank: storage.vtank,
  bulkbag: storage.bulkbag,
  octabin: storage.octabin,
  ibc: storage.ibc,
  supermixer: storage.supermixer,
  coldmixer: storage.coldmixer,
  buffertank: storage.buffertank,
  nitrogen: storage.nitrogen,
  bubblecolumn: storage.bubblecolumn,
  washcontainer: storage.washcontainer,

  // Feeders & Conveyors
  rotaryvalve: conveying.rotaryvalve,
  liwfeeder: conveying.liwfeeder,
  volfeeder: conveying.volfeeder,
  screwconv: conveying.screwconv,
  beltconv: conveying.beltconv,
  bagdump: conveying.bagdump,
  transferconv: conveying.transferconv,
  crusherconv: conveying.crusherconv,
  basketconv: conveying.basketconv,
  sidefeeder: conveying.sidefeeder,
  manualdebag: conveying.manualdebag,
  hoistdebag: conveying.hoistdebag,
  debaghopper: conveying.debaghopper,
  debagging: conveying.debagging,
  rawmatsilo: conveying.rawmatsilo,
  scsfeeder: conveying.scsfeeder,
  tcsfeeder: conveying.tcsfeeder,
  liquidfeeder: conveying.liquidfeeder,
  forcedfeeder: conveying.forcedfeeder,
  granulessilo: conveying.granulessilo,
  powdersilo: conveying.powdersilo,
  drytank: conveying.drytank,

  // Processing & Dies
  tse: process.tse,
  sse: process.sse,
  coknead: process.coknead,
  gearpump: process.gearpump,
  screenchange: process.screenchange,
  staticmix: process.staticmix,
  pelletscanner: process.pelletscanner,
  crosshead: process.crosshead,
  stranddie: process.stranddie,
  uwdie: process.uwdie,
  flatdie: process.flatdie,
  degassing: process.degassing,

  // Pneumatic conveying
  blower: conveying.blower,
  rootsblower: conveying.rootsblower,
  diverter2: conveying.diverter2,
  diverter3: conveying.diverter3,
  divertervalve: conveying.divertervalve,
  filterreceiver: conveying.filterreceiver,
  cyclone: conveying.cyclone,
  elbow90: conveying.elbow90,
  vacloader: conveying.vacloader,
  vacreceiver: conveying.vacreceiver,
  connectingtube: conveying.connectingtube,
  fluidizedbed: conveying.fluidizedbed,

  // Cable & compounding downstream
  coolingtrough: downstream.coolingtrough,
  cathaul: downstream.cathaul,
  sparktester: downstream.sparktester,
  odgauge: downstream.odgauge,
  winder: downstream.winder,
  accumulator: downstream.accumulator,
  preheater: downstream.preheater,
  eccmon: downstream.eccmon,
  capstan: downstream.capstan,
  strandbath: downstream.strandbath,
  airknife: downstream.airknife,
  strandpellet: downstream.strandpellet,
  uwpellet: downstream.uwpellet,
  pelletdryer: downstream.pelletdryer,
  classifier: downstream.classifier,
  fgsilo: downstream.fgsilo,
  bagstation: downstream.bagstation,
  centdryer: downstream.centdryer,
  vibroscreen: downstream.vibroscreen,
  dedusting: downstream.dedusting,
  hotcutpellet: downstream.hotcutpellet,
  waterringpellet: downstream.waterringpellet,
  centdewater: downstream.centdewater,
  aircoolingvib: downstream.aircoolingvib,

  // Film & PP line
  pprollers: downstream.pprollers,
  stretchoven: downstream.stretchoven,
  quenchbath: downstream.quenchbath,
  takeuprollers: downstream.takeuprollers,
  takeupwinder: downstream.takeupwinder,

  // Valves & instruments
  gatevalve: utility.gatevalve,
  ballvalve: utility.ballvalve,
  checkvalve: utility.checkvalve,
  ctrlvalve: utility.ctrlvalve,
  butterflyvalve: utility.butterflyvalve,
  solenoidvalve: utility.solenoidvalve,
  prv: utility.prv,
  pgauge: utility.pgauge,
  tsensor: utility.tsensor,
  flowmeter: utility.flowmeter,
  levelswitch: utility.levelswitch,

  // Utilities
  centpump: utility.centpump,
  vacpump: utility.vacpump,
  chiller: utility.chiller,
  compressor: utility.compressor,
  motor: utility.motor,
  controlpanel: utility.controlpanel,
  metaldet: downstream.metaldet,
  heatex: utility.heatex,
  shelltube: utility.shelltube,
  weighbatcher: utility.weighbatcher,
  airfilter: utility.airfilter,

  // Annotations
  zonebox: utility.zonebox,
  textlabel: utility.textlabel,
  processarrow: utility.processarrow,
  titleblock: utility.titleblock,
};

// ─── Depth rules (the axis the 2D drawing doesn't have) ──────────────────

/** Symbols whose plan section is round: as deep as they are wide. */
const ROUND = new Set([
  'silo', 'hopper', 'vtank', 'buffertank', 'cyclone', 'filterreceiver', 'vacloader',
  'vacreceiver', 'fgsilo', 'centdryer', 'classifier', 'pelletdryer', 'dedusting',
  'uwpellet', 'hotcutpellet', 'waterringpellet', 'centdewater', 'fluidizedbed',
  'vibroscreen', 'bubblecolumn', 'rawmatsilo', 'granulessilo', 'powdersilo', 'drytank',
  'airfilter', 'connectingtube', 'supermixer', 'bagstation', 'weighbatcher',
  'octabin',
]);

/** Long machines you walk beside: deeper than their 2D height suggests. */
const WIDE: Record<string, number> = {
  beltconv: 1.5, screwconv: 1.5, transferconv: 1.3, crusherconv: 1.3, basketconv: 1.3,
  coolingtrough: 1.4, strandbath: 1.4, quenchbath: 1.0, aircoolingvib: 1.1,
  pprollers: 1.4, takeuprollers: 1.4, takeupwinder: 1.0, stretchoven: 1.0,
  tse: 1.0, sse: 1.1, coknead: 1.1, cathaul: 1.1, coldmixer: 1.0,
};

/**
 * Symbols drawn in PLAN in 2D, where the box is a footprint rather than an
 * elevation. Their Z extent is simply the 2D height.
 */
const PLAN = new Set(['zonebox', 'textlabel', 'processarrow', 'titleblock', 'elbow90']);

/** The Z extent for a symbol whose 2D box is `w × h`. */
export function depthFor(
  type: string,
  w: number,
  h: number,
  label?: string,
  model3?: string,
): number {
  type = aliasFor(type, label, model3);
  if (PLAN.has(type)) return h;
  if (ROUND.has(type)) return w;
  const k = WIDE[type];
  if (k) return Math.max(16, h * k);
  return Math.max(16, Math.min(w, h) * 1.15);
}

// ─── Family fallbacks ────────────────────────────────────────────────────

/**
 * A generic skid-mounted process machine: machine-dark channel base frame,
 * JCC-navy bevelled body, a rounded cast top cover, the operator door on −Z
 * (HMI, e-stop, rating plate) and RF-flanged process nozzles on the flow axis.
 * This is what an unmapped or custom symbol becomes, and it is deliberately
 * good enough to sit next to the bespoke models — built from the same kit, so
 * it reads as the same factory.
 *
 * Extents and ports are v1's: ports W/E at ±0.6 w on the flow axis (the
 * nozzle flange faces), N on the top cover (0.96 h), S at the floor centre.
 */
const genericMachine: Builder = ({ w, h, d }): Model3 => {
  const bodyH = h * 0.72;
  const baseH = h * 0.14;
  const r = Math.min(w, d) * 0.12;
  const cy = baseH + bodyH * 0.55;
  const bw = w * 0.84;
  const bd = d * 0.82;
  const coverH = h * 0.1;
  const top = baseH + bodyH + coverH;
  // Operator door on −Z, standing 0.3 proud of the body.
  const doorW = bw * 0.46;
  const doorH = bodyH * 0.72;
  const door = rotateX(bevelBox(doorW, 0.3, doorH, 0.12), -Math.PI / 2);
  translate(door, -bw * 0.18, baseH + bodyH * 0.16 + doorH / 2, -bd / 2);
  const hmi = moveA(hmiPanel({ w: Math.min(doorW * 0.7, bodyH * 0.5), tilt: 0, bezel: 0.7 }), -bw * 0.18, baseH + bodyH * 0.5, -bd / 2 - 0.65);
  const es = estop(Math.max(0.6, Math.min(w, h) * 0.022));
  for (const q of es) {
    rotateX(q.mesh, -Math.PI / 2);
    translate(q.mesh, bw * 0.22, baseH + bodyH * 0.62, -bd / 2);
  }
  // Process nozzles: flange faces land exactly on the v1 port positions.
  const nzW = nozzleRF({ at: v3(-bw / 2, cy, 0), n: v3(-1, 0, 0), r, proj: w * 0.6 - bw / 2, mat: zm('trim') });
  const nzE = nozzleRF({ at: v3(bw / 2, cy, 0), n: v3(1, 0, 0), r, proj: w * 0.6 - bw / 2, mat: zm('trim') });
  return {
    parts: assy(
      p(baseFrame(w * 0.96, d * 0.94, baseH), zm('base')),
      p(translate(bevelBox(bw, bodyH, bd, Math.min(1.5, Math.max(0.35, 0.05 * Math.min(bw, bodyH, bd))), { grid: 30 }), 0, baseH, 0), zm('skin')),
      p(translate(roundBox(w * 0.7, coverH, d * 0.68, Math.min(w, d) * 0.06, { seg: 2, top: 'round' }), 0, baseH + bodyH, 0), zm('trim')),
      p(door, zm('skin')),
      hmi,
      es,
      namePlate({ at: v3(bw * 0.22, baseH + bodyH * 0.3, -bd / 2 - 0.05), n: v3(0, 0, -1), w: Math.max(3, bw * 0.12), h: Math.max(2, bodyH * 0.08) }),
      nzW.parts,
      nzE.parts,
    ),
    height: top,
    ports: {
      W: { p: v3(-w * 0.6, cy, 0), n: v3(-1, 0, 0), r },
      E: { p: v3(w * 0.6, cy, 0), n: v3(1, 0, 0), r },
      N: { p: v3(0, top, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** The generic machine, for the kit selftest's budget and envelope check. */
export const genericMachineBuilder: Builder = genericMachine;

/** Category → archetype. Chosen so a mis-categorised symbol still reads right. */
const FAMILY_FALLBACK: Record<string, Builder> = {
  'Storage & Silos': storage.vtank,
  'Feeders & Conveyors': conveying.volfeeder,
  Processing: genericMachine,
  'Dies & Heads': process.stranddie,
  'Cable Downstream': downstream.sparktester,
  'Compounding Downstream': downstream.classifier,
  'Pneumatic Conveying': conveying.vacreceiver,
  'Valves & Instruments': utility.gatevalve,
  Utilities: genericMachine,
  'Film & PP Line': downstream.pprollers,
  Annotations: utility.textlabel,
};

/**
 * Model aliases matched on the symbol's NAME rather than its type key.
 *
 * A custom symbol has a generated type key that means nothing to this module,
 * so a user-drawn "Octabin" would otherwise fall through to the generic
 * machine. Matching the label as well means naming a symbol after the thing it
 * is gets you the model of that thing, with no registration step.
 */
const NAME_ALIASES: [RegExp, string][] = [
  [/octa\s*-?\s*(bin|box)/i, 'octabin'],
  [/\bfibc\b|bulk\s*bag/i, 'bulkbag'],
  [/\bibc\b|tote\s*bin/i, 'ibc'],
  [/\bcyclone\b/i, 'cyclone'],
  [/\belbow\b/i, 'elbow90'],
];

/**
 * The type key a symbol should actually be modelled as.
 * An explicit `model3` wins; then the exact type; then the type key matched by
 * name; then the label.
 *
 * Note what the last two mean in practice: for anything WITHOUT an explicit
 * choice, the 3D form is inferred, and for a built-in symbol the label is not
 * consulted at all. So an `ibc` renamed "Octabin" keeps the IBC model, and a
 * custom symbol called "Octabin" loses the octagon the moment it is renamed.
 * That is why `model3` exists — inference stays the default, but a decision
 * made in the 3D view is recorded rather than re-guessed every frame.
 */
export function aliasFor(type: string, label?: string, model3?: string): string {
  if (model3 && BUILDERS[model3]) return model3;
  if (BUILDERS[type]) return type;
  for (const [re, target] of NAME_ALIASES) if (re.test(type)) return target;
  if (label) for (const [re, target] of NAME_ALIASES) if (re.test(label)) return target;
  return type;
}

/** Friendly names for models that have no 2D symbol of their own. */
const EXTRA_LABELS: Record<string, string> = {
  octabin: 'Octabin',
  shelltube: 'Heat Exchanger (Shell & Tube)',
};

// ─── Family-registered models (kit decision D5) ──────────────────────────
//
// Each family module may export `EXTRA_MODELS` (see `ExtraModel` in kit.ts).
// They are merged here, once, at module load — BEFORE `MODEL_CHOICES` is
// computed — so a new builder lands in the 3D Model picker with its label and
// depth rule without anyone editing this file. Registration is the whole of
// it: a family model is reachable only by pinning a shape to it (`model3`),
// never through `NAME_ALIASES`.
//
// INVARIANT: a family key never shadows a model in the table above (or an
// earlier family's key) — the explicit table wins, so an existing drawing can
// never change shape because a family added a model.

/** Every family module, in merge order (earlier wins on a key clash). */
const FAMILY_MODULES: object[] = [storage, conveying, process, downstream, utility, cable];

/** A module's optional export, read dynamically so a family without it is fine. */
function familyExport<T>(mod: object, name: string): T | undefined {
  const v = (mod as Record<string, unknown>)[name];
  return v && typeof v === 'object' ? (v as T) : undefined;
}

for (const mod of FAMILY_MODULES) {
  const extras = familyExport<ExtraModels>(mod, 'EXTRA_MODELS');
  if (!extras) continue;
  for (const [key, e] of Object.entries(extras)) {
    if (!e || typeof e.build !== 'function' || BUILDERS[key]) continue;
    BUILDERS[key] = e.build;
    if (!DEFS[key]) EXTRA_LABELS[key] = e.label;
    if (e.depth === 'round') ROUND.add(key);
    else if (e.depth === 'plan') PLAN.add(key);
    else if (typeof e.depth === 'number' && e.depth > 0) WIDE[key] = e.depth;
  }
}

/**
 * Ports / models a family has DECLARED as deliberately moved off the pre-KIT
 * baseline (`.claude/premium/kitprobe/model-baseline.json`), with the reason.
 * Each family module may export `BASELINE_MOVED: Record<string, string>`,
 * keyed `'model'` (extents/height) or `'model.PORT'` (one port). The models
 * selftest reports these instead of failing on them — the record of a
 * decision, kept next to the builder that made it.
 */
export function declaredMoves(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const mod of FAMILY_MODULES) {
    const m = familyExport<Record<string, string>>(mod, 'BASELINE_MOVED');
    if (m) for (const [k, v] of Object.entries(m)) if (!(k in out)) out[k] = String(v);
  }
  return out;
}

/** Every model a shape can be forced to, for the 3D view's model picker. */
export const MODEL_CHOICES: { key: string; label: string }[] = Object.keys(BUILDERS)
  .map((key) => ({ key, label: DEFS[key]?.label ?? EXTRA_LABELS[key] ?? key }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** Resolve the builder for a symbol type, falling back through its category. */
function builderFor(type: string): Builder {
  const exact = BUILDERS[type];
  if (exact) return exact;
  // Custom symbols live in the registry, not DEFS — check both for the group.
  const group = registry.getDef(type)?.g ?? DEFS[type]?.g;
  return (group && FAMILY_FALLBACK[group]) || genericMachine;
}

/** True when the type has a hand-built model (used by the stats readout). */
export function hasBespokeModel(type: string): boolean {
  return type in BUILDERS;
}

// ─── Cache ───────────────────────────────────────────────────────────────
//
// Building a silo is a few thousand floating-point operations; doing it per
// frame would be absurd. Models are keyed by type + size and reused across
// every shape that shares them, which on a real drawing is most of them.

const cache = new Map<string, Model3>();

/**
 * Enforce the one invariant every model has to satisfy: its lowest point rests
 * on the floor (y = 0).
 *
 * Getting this exactly right inside a hundred-odd builders is a losing game —
 * a saddle, a flange or a motor housing that hangs a few units low leaves the
 * machine buried in the concrete, and the error is invisible until you orbit
 * under it. Correcting it once, here, also covers the family fallbacks and any
 * custom symbol that ever routes through them.
 */
function groundModel(m: Model3): Model3 {
  let minY = Infinity;
  for (const part of m.parts)
    for (let i = 1; i < part.mesh.pos.length; i += 3)
      if (part.mesh.pos[i] < minY) minY = part.mesh.pos[i];
  if (!Number.isFinite(minY) || Math.abs(minY) < 0.05) return m;
  const dy = -minY;
  // Safe to mutate: builders return freshly-allocated meshes, and this runs
  // once per cache entry, before anything else can see the model.
  for (const part of m.parts) translate(part.mesh, 0, dy, 0);
  const ports: Model3['ports'] = {};
  for (const key of Object.keys(m.ports) as (keyof Model3['ports'])[]) {
    const v = m.ports[key];
    // Spread first so the optional nozzle radius (`Port3.r`) survives the lift.
    if (v) ports[key] = { ...v, p: { x: v.p.x, y: v.p.y + dy, z: v.p.z } };
  }
  return { ...m, ports, height: m.height + dy };
}

/**
 * Slide any port that is not touching its model onto the surface, along its own
 * outward normal.
 *
 * A port is where pipework docks, so a port floating in the air off the side of
 * a machine is a pipe that visibly stops short of it — the same defect as the
 * 2D elbow ports, and an audit found it on 42 of the 112 models. Getting every
 * one right by hand inside a hundred builders is the losing game `groundModel`
 * already exists to avoid, so it is corrected once, here.
 *
 * Only the distance ALONG the normal is corrected. The normal itself is left
 * alone, so a run still approaches the machine from the direction the builder
 * intended and only the dock point moves; and when the nearest metal is off to
 * one side rather than along the normal (a port beside a thin plate) the
 * projection is near zero and the port is left where the builder put it, which
 * is the safe answer rather than a guess.
 */
function dockPorts(m: Model3): Model3 {
  const keys = Object.keys(m.ports) as (keyof Model3['ports'])[];
  if (!keys.length || !m.parts.length) return m;
  // How far a port may travel. Generous enough to fix a real miss, tight enough
  // that a bad measurement cannot fling one across the machine.
  let span = 0;
  for (const part of m.parts)
    for (let i = 0; i < part.mesh.pos.length; i += 3)
      span = Math.max(span, Math.abs(part.mesh.pos[i]), Math.abs(part.mesh.pos[i + 2]));
  const limit = Math.max(6, span * 0.5);
  const ports: Model3['ports'] = {};
  for (const key of keys) {
    const port = m.ports[key];
    if (!port) continue;
    let best = Infinity;
    let bq = port.p;
    for (const part of m.parts) {
      const { q, d } = closestOnMesh(part.mesh, port.p);
      if (d < best) {
        best = d;
        bq = q;
      }
    }
    // Already on the machine, or nothing close enough to trust.
    if (best <= 1.2 || best > limit) {
      ports[key] = port;
      continue;
    }
    const t = (bq.x - port.p.x) * port.n.x + (bq.y - port.p.y) * port.n.y + (bq.z - port.p.z) * port.n.z;
    if (Math.abs(t) < 0.5 || Math.abs(t) > limit) {
      ports[key] = port;
      continue;
    }
    ports[key] = {
      ...port,
      p: { x: port.p.x + port.n.x * t, y: port.p.y + port.n.y * t, z: port.p.z + port.n.z * t },
    };
  }
  return { ...m, ports };
}

/**
 * The 3D model for a symbol of `type` at the 2D size `w × h`. Memoised.
 * Pass `label` so a custom symbol named after real equipment resolves to it.
 */
export function getModel(
  type: string,
  w: number,
  h: number,
  label?: string,
  variant?: string,
  model3?: string,
): Model3 {
  return getModelExact(aliasFor(type, label, model3), w, h, variant);
}

/**
 * The variant string for a shape, or undefined when it has only one 3D form.
 * The height is quantised so dragging it does not fill the model cache with
 * near-duplicates.
 */
export function variantOf(s: {
  type: string;
  h: number;
  mode3?: string;
  wallSide3?: string;
  wallH3?: number;
}): string | undefined {
  if (s.type !== 'zonebox') return undefined;
  const mode = s.mode3 ?? 'floor';
  if (mode === 'floor') return undefined;
  const hq = Math.round((s.wallH3 ?? s.h * 3) / 10) * 10;
  return `${mode}|${s.wallSide3 ?? 'N'}|${hq}`;
}

/** Convenience for the common case: the shape already knows its own form. */
export function getModelFor(s: {
  type: string;
  w: number;
  h: number;
  label?: string;
  model3?: string;
  mode3?: string;
  wallSide3?: string;
  wallH3?: number;
}): Model3 {
  return getModel(s.type, s.w, s.h, s.label, variantOf(s), s.model3);
}

/**
 * The Z extent for a shape, resolved the same way its model is.
 * Callers should prefer this over `depthFor`, so a shape's depth can never be
 * computed from a different model than the one actually drawn.
 */
export function depthForShape(s: {
  type: string;
  w: number;
  h: number;
  label?: string;
  model3?: string;
}): number {
  return depthFor(s.type, s.w, s.h, s.label, s.model3);
}

/**
 * The kit detail level every model is built at (kit spec §6.3). v2 ships with
 * full detail only; the hook exists so a future DOCUMENT-level switch (never a
 * camera-level one — that would rebuild while orbiting) can build heavy plants
 * at lod 1 without touching a single builder.
 */
const MODEL_LOD: 0 | 1 = 0;

function getModelExact(type: string, w: number, h: number, variant?: string): Model3 {
  // Quantise the size so a drag-resize doesn't thrash the cache with near
  // duplicates; 2 world units is well under one pixel at normal zoom.
  const qw = Math.max(6, Math.round(w / 2) * 2);
  const qh = Math.max(6, Math.round(h / 2) * 2);
  // INVARIANT: the detail level is part of the key, so a builder stays a pure
  // function of (Dims, lod) and two levels never share a cache entry.
  const lod = MODEL_LOD;
  const key = `${type}|${qw}|${qh}|${variant ?? ''}|L${lod}`;
  let m = cache.get(key);
  if (!m) {
    const d = depthFor(type, qw, qh);
    try {
      m = withDetail(lod, () => dockPorts(groundModel(builderFor(type)({ w: qw, h: qh, d, variant }))));
    } catch {
      // A model that throws must not take the whole view down with it.
      m = withDetail(lod, () => dockPorts(groundModel(genericMachine({ w: qw, h: qh, d }))));
    }
    if (cache.size > 400) cache.clear();
    cache.set(key, m);
  }
  return m;
}

export type { Model3, Builder } from './kit';
